import { Box } from "@/components/Box/Box";
import { DecorativeText } from "@/components/DecorativeText/DecorativeText";
import { Label } from "@/components/Label/Label";
import { Stack } from "@/components/Stack/Stack";
import { Typography } from "@/components/Typography/Typography";
import { LinkData } from "@/types/graphqlResponse";
import { useAnalytics } from "@/utils/DataAnalytics";
import { DataAnalyticsEvents } from "@/utils/DataAnalytics/models/DataAnalyticsDefinitions";

import styles from "./DecorativeCarCard.module.scss";
import { GenericLink } from "../GenericLink/GenericLink";

interface Model {
  /**
   * Car model displayed on the car and used in the decorativeText
   */
  model: string;
}

interface DisplayProps extends Model {
  /**
   * Image filling the DecorativeText component
   */
  backgroundImage: string | null;

  /**
   * Car model image sitting in front of the decorative text
   */
  displayImage: string | null;
}

/**
 * Note: Do not do more than 3 characters and find appropriate way of cropping content on consumer
 */
const Display: React.FC<DisplayProps> = ({ model, backgroundImage, displayImage }) => {
  const modelTrimmed = model.split(" ")[0].slice(0, 3);
  return (
    <Stack className={styles.display}>
      {backgroundImage ? (
        <div className={styles.decorativeText}>
          <DecorativeText backgroundImage={backgroundImage} text={modelTrimmed} />
        </div>
      ) : null}
      {displayImage ? <img className={styles.image} src={displayImage} alt={model} /> : null}
    </Stack>
  );
};

interface DetailsProps extends Model {
  /**
   * Car body type
   */
  modelName: string;
}
const Details: React.FC<DetailsProps> = ({ modelName, model }) => (
  <Stack direction="column" spacing="3xs" className={styles.content}>
    <Typography variant="h5">{model}</Typography>
    <Stack spacing="3xs" direction="column">
      <Label>{modelName}</Label>
    </Stack>
  </Stack>
);

type DecorativeCarCardProps = DisplayProps &
  DetailsProps &
  Required<React.PropsWithChildren<{ cta: LinkData }>>;

/**
 * DecorativeCarCard
 * 
 * `GenericLink` must wrap child buttons in order to allow consumer to choose what sort of link/routing to execute
 *
 * ## Usage
 *
 * ```tsx
 * import image from "@/assets/stories/diagnostic-image.jpg"
 * ...
 * <DecorativeCarCard
 *  displayImage={image}
 *  backgroundImage={image}
 *  model='NX'
 *  modelName='SUV'
 *  cta={model.cta1}
 *  >
 *   <GenericLink
        asWrapper
        href={model.cta2.url}
        target={model.cta2.target || undefined}
      >
        {model.cta2.label}
      </GenericLink>
      <GenericLink
        asWrapper
        href={model.cta1.url}
        target={model.cta1.target || undefined}
      >
        {model.cta1.label}
      </GenericLink>
 * </DecorativeCarCard>
 * ```
 */
export const DecorativeCarCard: React.FC<DecorativeCarCardProps> = ({
  model,
  backgroundImage,
  displayImage,
  modelName: bodyType,
  children,
  cta,
}) => {
  const { registerEvent } = useAnalytics();

  const handleClick = () => {
    const data = {
      event: "modelMenuModelClick",
      componentLink: cta.url,
    };
    registerEvent(DataAnalyticsEvents.CLICK, data);
  };

  return (
    <Box spacing="3xs" p="s" className={styles.container}>
      <GenericLink
        asWrapper
        href={cta.url}
        label={cta.label || ""}
        onClick={handleClick}
        className={styles.mainContent}
      >
        <Display backgroundImage={backgroundImage} displayImage={displayImage} model={model} />
        <Details model={model} modelName={bodyType} />
      </GenericLink>
      <Stack pt="m" spacing="m" className={styles.buttons}>
        {children}
      </Stack>
    </Box>
  );
};
